// hooks/useUrlState.js
// One canonical wrapper over react-router's useSearchParams so every tab /
// subtab / filter / selection on the scheduler reads and writes the URL the
// same way. This replaces the two ad-hoc mechanisms that grew up in the app
// (raw `history.replaceState` in EmployeeProfile, hand-built `navigate('?...')`
// strings in the grid). See docs/navigation.md for the param vocabulary.
//
// Behaviour:
//  - Default-omission: when the value equals `defaultValue`, the param is
//    DELETED from the URL rather than written. Keeps bare URLs clean
//    (`/schedule/grid`, not `/schedule/grid?view=people&dept=mine&...`).
//  - `replace` (default true) for view-state, so toggling a filter doesn't add
//    a back-button entry. Pass `{ replace: false }` for entity selection
//    (`?job=`, `?staff=`) so the Back button closes the selection.
//  - Reads only on mount / param change; never writes on mount, so an incoming
//    deeplink is honoured but a bare URL stays bare.
//  - Multiple hooks on one page are safe: writes clone the existing params and
//    touch only their own key.

import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

function defaultSerialize(value) {
  if (typeof value === 'boolean') return value ? '1' : '0';
  return String(value);
}

function makeDefaultDeserialize(defaultValue) {
  return (raw) => {
    if (typeof defaultValue === 'boolean') return raw === '1' || raw === 'true';
    if (typeof defaultValue === 'number') {
      const n = Number(raw);
      return Number.isNaN(n) ? defaultValue : n;
    }
    return raw; // strings (and anything caller deserializes itself)
  };
}

function isValid(value, validate) {
  if (validate == null) return true;
  if (Array.isArray(validate)) return validate.includes(value);
  if (typeof validate === 'function') return validate(value);
  return true;
}

// ── Module-level write accumulation ──────────────────────────────────────────
// react-router's setSearchParams(fn) feeds `fn` the params captured at RENDER
// time, and consecutive synchronous calls don't compose — so two URL writes in
// one event handler clobber (the second navigate replaces the first, dropping
// its key). We sidestep that entirely: every useUrlState write lands in ONE
// shared draft seeded from the live URL, flushed once per tick as a single
// absolute write. This composes all synchronous writes — even across different
// components (e.g. a parent's ?tab and a child's ?job) — and is immune to stale
// render-time closures.
let pendingParams = null;   // URLSearchParams draft for the current tick
let pendingReplace = true;  // push if ANY writer this tick requested push
let flushScheduled = false;
let flushSetter = null;     // any hook's setSearchParams (all navigate identically)

function scheduleFlush(setSearchParams) {
  flushSetter = setSearchParams;
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    const params = pendingParams;
    const replace = pendingReplace;
    const setter = flushSetter;
    pendingParams = null;
    pendingReplace = true;
    flushScheduled = false;
    flushSetter = null;
    // Pass an absolute URLSearchParams (NOT a function) so react-router uses it
    // verbatim, ignoring its render-time closure params.
    setter(params, { replace });
  });
}

/**
 * URL-backed view state, ergonomically like useState.
 *
 * @template T
 * @param {string} key                              query-param name
 * @param {T} defaultValue                          value when the param is absent/invalid
 * @param {object} [opts]
 * @param {boolean} [opts.replace=true]             replace (no history entry) vs push
 * @param {(v:T)=>string} [opts.serialize]          custom encoder (default by typeof)
 * @param {(s:string)=>T} [opts.deserialize]        custom decoder (default by typeof defaultValue)
 * @param {T[]|((v:T)=>boolean)} [opts.validate]    allow-list or predicate; invalid → defaultValue
 * @returns {[T, (next: T | ((cur:T)=>T)) => void]}
 */
export function useUrlState(key, defaultValue, opts = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Latest config in a ref so `setValue` keeps a stable identity across renders
  // even when callers pass inline serialize/validate functions — safe to drop
  // into effect dependency arrays.
  const cfg = useRef(null);
  cfg.current = {
    defaultValue,
    replace: opts.replace !== undefined ? opts.replace : true,
    serialize: opts.serialize || defaultSerialize,
    deserialize: opts.deserialize || makeDefaultDeserialize(defaultValue),
    validate: opts.validate,
  };

  const raw = searchParams.get(key);
  // Memoize on the raw string so an unchanged param yields a stable reference —
  // critical for array/object values dropped into effect / useMemo dep arrays.
  const value = useMemo(() => {
    const { deserialize, validate, defaultValue: dflt } = cfg.current;
    if (raw == null) return dflt;
    const parsed = deserialize(raw);
    return isValid(parsed, validate) ? parsed : dflt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  const setValue = useCallback((next) => {
    const { defaultValue: dflt, replace, serialize, deserialize } = cfg.current;
    // Seed the shared draft from the LIVE url on the first write of this tick,
    // then mutate only this key. Subsequent writes (this key or others, this
    // component or another) accumulate into the same draft.
    if (pendingParams == null) pendingParams = new URLSearchParams(window.location.search);
    const prevRaw = pendingParams.get(key);
    const current = prevRaw != null ? deserialize(prevRaw) : dflt;
    const resolved = typeof next === 'function' ? next(current) : next;
    if (serialize(resolved) === serialize(dflt)) {
      pendingParams.delete(key); // default-omission
    } else {
      pendingParams.set(key, serialize(resolved));
    }
    if (!replace) pendingReplace = false;
    scheduleFlush(setSearchParams);
  }, [key, setSearchParams]);

  return [value, setValue];
}

// Convenience serializers for common non-scalar params (e.g. ?staff=1,2,3).
//
// A lone scalar is treated as a one-item list rather than serialized to ''.
// The old `: ''` fallback lost the value silently: '' round-trips through
// deserialize as [], so a caller that passed `setDeptFilter('all')` instead of
// `setDeptFilter(['all'])` didn't get "all" — it got an EMPTY filter, and the
// grid rendered zero rows. That was the Conflicts-tab blank-grid bug. Only
// null/undefined/'' mean "no value".
const csvSerialize = (v) => {
  if (Array.isArray(v)) return v.join(',');
  return v == null || v === '' ? '' : String(v);
};

export const csvOfNumbers = {
  serialize: csvSerialize,
  deserialize: (s) => (s ? s.split(',').map(Number).filter((n) => !Number.isNaN(n)) : []),
};

export const csvOfStrings = {
  serialize: csvSerialize,
  deserialize: (s) => (s ? s.split(',') : []),
};

export default useUrlState;
